/**
 * Template refs resolve to a DOM node when bound to a plain element and to a component instance when
 * bound to a component (`USkeleton`, `UMain`, `UFormField`, …). Anything that hands a ref to a browser
 * API (observers, scroll math, measurement) has to collapse those two shapes to one element first.
 */

/** What a template ref can hold: a DOM node, a component instance, or nothing yet. */
export type TemplateRefValue = Element | { $el?: unknown } | null | undefined

/**
 * Element behind a template ref, or `null` when the ref is unset or the component has no element root
 * (a text or comment root, which observers cannot accept).
 */
export function resolveTemplateRefElement(value: TemplateRefValue): Element | null {
    if (!value) return null
    if (value instanceof Element) return value
    const el = (value as { $el?: unknown }).$el
    return el instanceof Element ? el : null
}

/**
 * Like {@link resolveTemplateRefElement}, narrowed to `HTMLElement` for callers that need HTML-only
 * members such as `scrollTop` or `offsetHeight`. SVG roots resolve to `null`.
 */
export function resolveTemplateRefHtmlElement(value: TemplateRefValue): HTMLElement | null {
    const el = resolveTemplateRefElement(value)
    return el instanceof HTMLElement ? el : null
}
